import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MESES = [
  "enero","febrero","marzo","abril","mayo","junio",
  "julio","agosto","septiembre","octubre","noviembre","diciembre",
];

const OPERADORES = [
  "Aportes en Línea",
  "Mi Planilla",
  "SOI (Servicios Operativos de Información)",
  "ARUS",
  "Compensar PILA",
  "Enlace Operativo",
  "Asopagos",
  "Pago Simple",
  "Simple S.A.S",
  "Porvenir",
  "Bancolombia",
  "Banco de Bogotá",
  "Banco de Occidente",
  "Banco Davivienda",
  "Banco Popular",
  "Banco AV Villas",
  "Banco Caja Social",
  "Banco Itaú",
  "Banco BBVA",
  "Banco Agrario de Colombia",
  "Scotiabank Colpatria",
];

const PERIODOS_NO_APLICA = new Set(["DECRETO 1273 DE 2018", "N/A"]);

const toInputDate = (v) => (v ? String(v).substring(0, 10) : "");
const isValidDateOrder = (ini, fin) => {
  if (!ini || !fin) return true; // si no está completo, no bloquea
  return new Date(ini) <= new Date(fin);
};

const RegistrarInformeContractual = () => {
  const { id, informeId } = useParams();
  const navigate = useNavigate();

  const isEdit = useMemo(() => Boolean(informeId), [informeId]);

  const [loading, setLoading] = useState(false);

  // ✅ Estado unificado para evitar inconsistencias
  const [form, setForm] = useState({
    ciudad: "",
    fechaInforme: "",
    mes: "",
    anio: new Date().getFullYear(),

    periodoPlanilla: "",
    numeroPlanilla: "",
    operadorPlanilla: "",

    activeTab: "obligaciones",

    obligaciones: [],
    acciones: [],
    evidencias: [],

    desplazamientos: [],
  });

  const {
    ciudad,
    fechaInforme,
    mes,
    anio,

    periodoPlanilla,
    numeroPlanilla,
    operadorPlanilla,

    activeTab,
    obligaciones,
    acciones,
    evidencias,
    desplazamientos,
  } = form;

  const disabledAll = loading;

  const isPeriodoNA = useMemo(() => PERIODOS_NO_APLICA.has(periodoPlanilla), [periodoPlanilla]);

  // ---------------------------------------------------------
  // Helpers de actualización de estado
  // ---------------------------------------------------------
  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const cleanData = useCallback(() => {
    setForm({
      ciudad: "",
      fechaInforme: "",
      mes: "",
      anio: new Date().getFullYear(),

      periodoPlanilla: "",
      numeroPlanilla: "",
      operadorPlanilla: "",

      activeTab: "obligaciones",

      obligaciones: [],
      acciones: [],
      evidencias: [],

      desplazamientos: [],
    });
  }, []);

  // ---------------------------------------------------------
  // ✅ Si NO aplica Planilla => limpiar campos dependientes
  // ---------------------------------------------------------
  useEffect(() => {
    if (!isPeriodoNA) return;

    setForm((prev) => ({
      ...prev,
      numeroPlanilla: "",
      operadorPlanilla: "",
    }));
  }, [isPeriodoNA]);

  const handlePeriodoPlanillaChange = useCallback((e) => {
    const value = e.target.value;
    setForm((prev) => {
      const noAplica = PERIODOS_NO_APLICA.has(value);
      return {
        ...prev,
        periodoPlanilla: value,
        numeroPlanilla: noAplica ? "" : prev.numeroPlanilla,
        operadorPlanilla: noAplica ? "" : prev.operadorPlanilla,
      };
    });
  }, []);

  // ---------------------------------------------------------
  // Cargar informe (solo edición)
  // ---------------------------------------------------------
  useEffect(() => {
    const cargarDatosInforme = async () => {
      if (!isEdit) return;

      try {
        setLoading(true);
        const res = await axios.get(`/api/informecontractual/listid/${informeId}`);
        const datos = res.data?.informe;
        if (!datos) return;

        const obligacionesRaw = datos.obligaciones || [];

        const obligacionesMapped = obligacionesRaw.map((ob) => ({
          _id: ob._id,
          obligacionid: ob.obligacionid?._id || ob.obligacionid,
          observacion: ob.obligacionid?.observacion || "",
          descripcion: ob.obligacionid?.descripcion || "",
        }));

        const accionesMapped = obligacionesRaw.map((ob) => ob.accion || "");
        const evidenciasMapped = obligacionesRaw.map((ob) => ob.evidencia || "");

        setForm((prev) => ({
          ...prev,
          ciudad: datos.ciudad || "",
          fechaInforme: toInputDate(datos.fechaInforme),
          mes: datos.mes || "",
          anio: Number(datos.anio || new Date().getFullYear()),

          periodoPlanilla: datos.periodoPlanilla || "",
          numeroPlanilla: datos.numeroPlanilla || "",
          operadorPlanilla: datos.operadorPlanilla || "",

          desplazamientos: datos.desplazamientos || [],

          obligaciones: obligacionesMapped,
          acciones: accionesMapped,
          evidencias: evidenciasMapped,
        }));
      } catch (error) {
        console.error("Error al cargar el informe:", error);
        Swal.fire("Error", "No se pudo cargar el informe.", "error");
      } finally {
        setLoading(false);
      }
    };

    cargarDatosInforme();
  }, [informeId, isEdit]);

  // ---------------------------------------------------------
  // Cargar obligaciones (solo creación)
  // ---------------------------------------------------------
  useEffect(() => {
    const obtenerObligaciones = async () => {
      if (!id || isEdit) return;

      try {
        setLoading(true);
        const res = await axios.get(`/api/obligaciones/contrato/${id}`);
        const datos = res.data;

        if (!datos?.ok) return;

        const lista = (datos.obligaciones || []).map((item) => ({
          obligacionid: item._id,
          observacion: item.observacion || "",
          descripcion: item.descripcion || "",
        }));

        setForm((prev) => ({
          ...prev,
          obligaciones: lista,
          acciones: Array(lista.length).fill(""),
          evidencias: Array(lista.length).fill(""),
        }));
      } catch (error) {
        console.error("Error al obtener obligaciones:", error);
        Swal.fire("Error", "No se pudieron cargar las obligaciones.", "error");
      } finally {
        setLoading(false);
      }
    };

    obtenerObligaciones();
  }, [id, isEdit]);

  // ---------------------------------------------------------
  // ✅ Validaciones
  // ---------------------------------------------------------
  const validarPlanilla = useCallback(() => {
    if (!periodoPlanilla) {
      Swal.fire({ icon: "warning", title: "Validación", text: 'Seleccione "Periodo Planilla".' });
      return false;
    }

    if (isPeriodoNA) {
      if ((numeroPlanilla || "").trim() || (operadorPlanilla || "").trim()) {
        Swal.fire({
          icon: "warning",
          title: "Validación de Planilla",
          text: 'Para "DECRETO 1273 DE 2018" o "N/A", Número y Operador deben estar vacíos.',
        });
        return false;
      }
      return true;
    }

    if (!(numeroPlanilla || "").trim() || !(operadorPlanilla || "").trim()) {
      Swal.fire({
        icon: "warning",
        title: "Validación de Planilla",
        text: 'Debe diligenciar "Número de Planilla" y "Operador Planilla".',
      });
      return false;
    }

    return true;
  }, [periodoPlanilla, isPeriodoNA, numeroPlanilla, operadorPlanilla]);

  const validarDesplazamientos = useCallback(() => {
    for (let i = 0; i < desplazamientos.length; i++) {
      const d = desplazamientos[i];
      const ini = toInputDate(d.fechaInicio);
      const fin = toInputDate(d.fechaFinal);

      if (!isValidDateOrder(ini, fin)) {
        Swal.fire({
          icon: "warning",
          title: "Validación de desplazamientos",
          text: `En la fila ${i + 1}, la fecha inicial no puede ser mayor que la fecha final.`,
        });
        return false;
      }
    }
    return true;
  }, [desplazamientos]);

  const validarObligacionesSync = useCallback(() => {
    if (obligaciones.length !== acciones.length || obligaciones.length !== evidencias.length) {
      Swal.fire({
        icon: "error",
        title: "Error interno",
        text: "Las acciones/evidencias no coinciden con el número de obligaciones. Recarga la página.",
      });
      return false;
    }
    return true;
  }, [obligaciones.length, acciones.length, evidencias.length]);

  // ---------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------
  const handleAccionChange = useCallback((index, value) => {
    setForm((prev) => {
      const next = [...prev.acciones];
      next[index] = value;
      return { ...prev, acciones: next };
    });
  }, []);

  const handleEvidenciaChange = useCallback((index, value) => {
    setForm((prev) => {
      const next = [...prev.evidencias];
      next[index] = value;
      return { ...prev, evidencias: next };
    });
  }, []);

  const handleDesplazamientoChange = useCallback((index, field, value) => {
    setForm((prev) => {
      const next = [...prev.desplazamientos];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, desplazamientos: next };
    });
  }, []);

  const agregarFilaDesplazamiento = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      desplazamientos: [
        ...prev.desplazamientos,
        { ordenViajeNo: "", lugar: "", fechaInicio: "", fechaFinal: "" },
      ],
    }));
  }, []);

  const eliminarFilaDesplazamiento = useCallback((index) => {
    setForm((prev) => ({
      ...prev,
      desplazamientos: prev.desplazamientos.filter((_, i) => i !== index),
    }));
  }, []);

  // ---------------------------------------------------------
  // Payload builders
  // ---------------------------------------------------------
  const planillaPayload = useMemo(() => {
    return {
      periodoPlanilla: (periodoPlanilla || "").trim(),
      numeroPlanilla: isPeriodoNA ? "" : (numeroPlanilla || "").trim(),
      operadorPlanilla: isPeriodoNA ? "" : (operadorPlanilla || "").trim(),
    };
  }, [periodoPlanilla, numeroPlanilla, operadorPlanilla, isPeriodoNA]);

  const obligacionesPayload = useMemo(() => {
    return obligaciones.map((ob, index) => ({
      ...(isEdit ? { _id: ob._id } : {}),
      obligacionid: ob.obligacionid,
      accion: (acciones[index] || "").trim(),
      evidencia: (evidencias[index] || "").trim(),
    }));
  }, [obligaciones, acciones, evidencias, isEdit]);

  const desplazamientosPayload = useMemo(() => {
    return (desplazamientos || []).map((d) => ({
      ...(d._id ? { _id: d._id } : {}),
      ordenViajeNo: (d.ordenViajeNo || d.orden || "").trim(),
      lugar: (d.lugar || "").trim(),
      fechaInicio: toInputDate(d.fechaInicio),
      fechaFinal: toInputDate(d.fechaFinal),
    }));
  }, [desplazamientos]);

  // ---------------------------------------------------------
  // Guardar / Actualizar
  // ---------------------------------------------------------
  const saveInforme = useCallback(async () => {
    try {
      if (!validarPlanilla()) return;
      if (!validarObligacionesSync()) return;
      if (!validarDesplazamientos()) return;

      const payload = {
        contratoId: id,
        ciudad: (ciudad || "").trim(),
        fechaInforme,
        mes,
        anio: Number(anio),
        ...planillaPayload,
        obligaciones: obligacionesPayload,
        desplazamientos: desplazamientosPayload,
      };

      setLoading(true);
      await axios.post("/api/informecontractual/add", payload);

      await Swal.fire({
        icon: "success",
        title: "Informe contractual registrado",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(-1);
    } catch (error) {
      console.error("Error al guardar informe:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo guardar el informe.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    id,
    ciudad,
    fechaInforme,
    mes,
    anio,
    planillaPayload,
    obligacionesPayload,
    desplazamientosPayload,
    validarPlanilla,
    validarObligacionesSync,
    validarDesplazamientos,
    cleanData,
    navigate,
  ]);

  const updateInforme = useCallback(async () => {
    try {
      if (!validarPlanilla()) return;
      if (!validarObligacionesSync()) return;
      if (!validarDesplazamientos()) return;

      const payload = {
        contratoId: id,
        ciudad: (ciudad || "").trim(),
        fechaInforme,
        mes,
        anio: Number(anio),
        ...planillaPayload,
        obligaciones: obligacionesPayload,
        desplazamientos: desplazamientosPayload,
      };

      setLoading(true);
      await axios.put(`/api/informecontractual/update/${informeId}`, payload);

      await Swal.fire({
        icon: "success",
        title: "Informe actualizado correctamente",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(-1);
    } catch (error) {
      console.error("Error al actualizar el informe:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "No se pudo actualizar el informe.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    id,
    informeId,
    ciudad,
    fechaInforme,
    mes,
    anio,
    planillaPayload,
    obligacionesPayload,
    desplazamientosPayload,
    validarPlanilla,
    validarObligacionesSync,
    validarDesplazamientos,
    cleanData,
    navigate,
  ]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isEdit) return updateInforme();
      return saveInforme();
    },
    [isEdit, saveInforme, updateInforme]
  );

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------
  return (
    <div className="container-fluid px-2 px-md-4">
      <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-3 px-md-4 py-3 border-bottom bg-white">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h5 className="mt-2 mb-1 fw-semibold">
                {isEdit ? "Editar Informe Contractual Mensual" : "Registrar Informe Contractual Mensual"}
              </h5>
              <small className="text-secondary">
                Completa los datos generales, obligaciones y desplazamientos del periodo.
              </small>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
                disabled={disabledAll}
                title="Volver"
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>

              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={cleanData}
                disabled={disabledAll}
                title="Limpiar"
              >
                <i className="fa-solid fa-eraser me-2"></i>
                Limpiar
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 p-md-4">
          {loading && (
            <div className="text-center text-secondary py-3">
              <div className="spinner-border" role="status" />
              <div className="mt-2">Procesando…</div>
            </div>
          )}

          <form id="form-informe" onSubmit={onSubmit}>
            {/* Datos generales */}
            <div className="border rounded-4 p-3 p-md-4 mb-3">
              <div className="fw-semibold mb-2">Datos Generales</div>

              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Ciudad</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Medellín"
                    value={ciudad}
                    onChange={(e) => setField("ciudad", e.target.value)}
                    required
                    disabled={disabledAll}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Fecha del Informe</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fechaInforme}
                    onChange={(e) => setField("fechaInforme", e.target.value)}
                    required
                    disabled={disabledAll}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Mes</label>
                  <select
                    className="form-select"
                    value={mes}
                    onChange={(e) => setField("mes", e.target.value)}
                    required
                    disabled={disabledAll}
                  >
                    <option value="">Seleccione…</option>
                    {MESES.map((nombre) => (
                      <option key={nombre} value={nombre}>
                        {nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Año</label>
                  <input
                    type="number"
                    className="form-control"
                    value={anio}
                    onChange={(e) => setField("anio", Number(e.target.value || new Date().getFullYear()))}
                    min="2000"
                    max="2100"
                    required
                    disabled={disabledAll}
                  />
                </div>
              </div>
            </div>

            {/* Planilla */}
            <div className="border rounded-4 p-3 p-md-4 mb-3">
              <div className="fw-semibold mb-2">Planilla</div>

              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Periodo Planilla</label>
                  <select
                    className="form-select"
                    value={periodoPlanilla}
                    onChange={handlePeriodoPlanillaChange}
                    required
                    disabled={disabledAll}
                  >
                    <option value="">Seleccione…</option>
                    <option value="DECRETO 1273 DE 2018">DECRETO 1273 DE 2018</option>
                    <option value="N/A">N/A No Aplica</option>
                    {MESES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  {isPeriodoNA && <div className="form-text">No aplica para este periodo.</div>}
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Núm Planilla</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: 123456789"
                    value={numeroPlanilla}
                    onChange={(e) => setField("numeroPlanilla", e.target.value)}
                    required={!isPeriodoNA}
                    disabled={disabledAll || isPeriodoNA}
                  />
                  {isPeriodoNA && <div className="form-text">No aplica para este periodo.</div>}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Operador Planilla</label>
                  <select
                    className="form-select"
                    value={operadorPlanilla}
                    onChange={(e) => setField("operadorPlanilla", e.target.value)}
                    required={!isPeriodoNA}
                    disabled={disabledAll || isPeriodoNA}
                  >
                    <option value="">Seleccione…</option>
                    {OPERADORES.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                  {isPeriodoNA && <div className="form-text">No aplica para este periodo.</div>}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "obligaciones" ? "active" : ""}`}
                  onClick={() => setField("activeTab", "obligaciones")}
                  disabled={disabledAll}
                >
                  <i className="fa-solid fa-list-check me-2"></i>
                  Obligaciones
                </button>
              </li>

              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "desplazamientos" ? "active" : ""}`}
                  onClick={() => setField("activeTab", "desplazamientos")}
                  disabled={disabledAll}
                >
                  <i className="fa-solid fa-route me-2"></i>
                  Desplazamientos
                </button>
              </li>
            </ul>

            {/* Obligaciones */}
            {activeTab === "obligaciones" && (
              <div>
                <div className="fw-semibold mb-2">Obligaciones Específicas</div>

                {obligaciones.length === 0 ? (
                  <div className="text-secondary">No hay obligaciones cargadas.</div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {obligaciones.map((ob, index) => (
                      <div key={ob._id || ob.obligacionid || index} className="border rounded-4 p-3 bg-white">
                        <div className="row g-3 align-items-start">
                          <div className="col-12 col-md-1 text-center">
                            <div className="badge text-bg-dark px-3 py-2 mt-1">{index + 1}</div>
                          </div>

                          <div className="col-12 col-md-3">
                            <label className="form-label fw-semibold d-md-none">Obligación</label>
                            <div className="form-control bg-light" style={{ minHeight: 58 }}>
                              {ob.observacion}
                            </div>
                          </div>

                          <div className="col-12 col-md-4">
                            <label className="form-label fw-semibold">Acciones Realizadas</label>
                            <textarea
                              className="form-control"
                              rows="4"
                              value={acciones[index] || ""}
                              onChange={(e) => handleAccionChange(index, e.target.value)}
                              placeholder="Describa las acciones realizadas"
                              required
                              disabled={disabledAll}
                            />
                          </div>

                          <div className="col-12 col-md-4">
                            <label className="form-label fw-semibold">Evidencias</label>
                            <textarea
                              className="form-control"
                              rows="4"
                              value={evidencias[index] || ""}
                              onChange={(e) => handleEvidenciaChange(index, e.target.value)}
                              placeholder="Describa las evidencias"
                              required
                              disabled={disabledAll}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Desplazamientos */}
            {activeTab === "desplazamientos" && (
              <div>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <div className="fw-semibold">Registro de Desplazamientos</div>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={agregarFilaDesplazamiento}
                    disabled={disabledAll}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Agregar Fila
                  </button>
                </div>

                {desplazamientos.length === 0 ? (
                  <div className="text-secondary">No hay desplazamientos. Puedes agregar una fila.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {desplazamientos.map((item, index) => (
                      <div key={item._id || index} className="border rounded-4 p-3 bg-white">
                        <div className="row g-3 align-items-end">
                          <div className="col-12 col-md-3">
                            <label className="form-label fw-semibold">Orden de Viaje</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Orden de Viaje"
                              value={item.ordenViajeNo || ""}
                              onChange={(e) => handleDesplazamientoChange(index, "ordenViajeNo", e.target.value)}
                              disabled={disabledAll}
                            />
                          </div>

                          <div className="col-12 col-md-4">
                            <label className="form-label fw-semibold">Lugar</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Lugar"
                              value={item.lugar || ""}
                              onChange={(e) => handleDesplazamientoChange(index, "lugar", e.target.value)}
                              disabled={disabledAll}
                            />
                          </div>

                          <div className="col-12 col-md-2">
                            <label className="form-label fw-semibold">Fecha Inicial</label>
                            <input
                              type="date"
                              className="form-control"
                              value={toInputDate(item.fechaInicio)}
                              onChange={(e) => handleDesplazamientoChange(index, "fechaInicio", e.target.value)}
                              disabled={disabledAll}
                            />
                          </div>

                          <div className="col-12 col-md-2">
                            <label className="form-label fw-semibold">Fecha Final</label>
                            <input
                              type="date"
                              className="form-control"
                              value={toInputDate(item.fechaFinal)}
                              onChange={(e) => handleDesplazamientoChange(index, "fechaFinal", e.target.value)}
                              disabled={disabledAll}
                            />
                          </div>

                          <div className="col-12 col-md-1 text-end">
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => eliminarFilaDesplazamiento(index)}
                              disabled={disabledAll}
                              title="Eliminar"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Footer botones */}
            <div className="d-flex flex-wrap gap-2 justify-content-end pt-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
                disabled={disabledAll}
                title="Cancelar"
              >
                <i className="fa-solid fa-xmark me-2"></i>
                Cancelar
              </button>

              <button
                type="submit"
                form="form-informe"
                className="btn btn-dark"
                disabled={disabledAll}
                title={isEdit ? "Actualizar" : "Guardar"}
              >
                <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"} me-2`}></i>
                {isEdit ? "Actualizar" : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrarInformeContractual;
